import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePractice } from '../context/PracticeContext';
import { useTimezone } from '../context/TimezoneContext';
import api from '../services/api';

function PlatformHeader({ isAdminView = false, isMobile = false, onMenuToggle }) {
  const { user, logout, isSuperAdmin } = useAuth();
  const { selectedPracticeId, selectPractice, practices, selectedPractice, clearSelection } = usePractice();
  const { displayMode, setDisplayMode, DISPLAY_MODES } = useTimezone();
  const navigate = useNavigate();

  const [showPracticeMenu, setShowPracticeMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [testMode, setTestMode] = useState(null);
  const practiceMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (practiceMenuRef.current && !practiceMenuRef.current.contains(event.target)) {
        setShowPracticeMenu(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch test mode status on mount
  useEffect(() => {
    api.getTestModeStatus().then(res => {
      if (res?.data?.testMode) setTestMode(res.data);
    }).catch(() => {});
  }, []);

  const handlePracticeChange = (practiceId) => {
    selectPractice(practiceId);
    setShowPracticeMenu(false);
  };

  const u = user?.user; // nested user object from session context

  const getUserInitials = () => {
    if (!u) return '?';
    const first = u.firstName ? u.firstName.charAt(0).toUpperCase() : '';
    const last = u.lastName ? u.lastName.charAt(0).toUpperCase() : '';
    return first + last || u.email?.charAt(0).toUpperCase() || '?';
  };

  const getUserDisplayName = () => {
    if (!u) return '';
    if (u.firstName && u.lastName) {
      const fullName = `${u.firstName} ${u.lastName}`;
      return fullName.length > 20 ? fullName.substring(0, 17) + '...' : fullName;
    }
    return u.email?.substring(0, 20) || '';
  };

  const getPracticeName = () => {
    if (selectedPractice) {
      const maxLen = isMobile ? 14 : 25;
      const truncLen = isMobile ? 11 : 22;
      return selectedPractice.name?.length > maxLen
        ? selectedPractice.name.substring(0, truncLen) + '...'
        : selectedPractice.name;
    }
    return 'Select Practice';
  };

  return (
    <header style={isAdminView ? styles.headerAdmin : styles.header}>
      {testMode && (
        <div style={styles.testModeBanner}>
          TEST MODE — All SMS, emails, and voice calls are redirected
          {testMode.redirectPhone && <span> | Phone: {testMode.redirectPhone}</span>}
          {testMode.redirectEmail && <span> | Email: {testMode.redirectEmail}</span>}
        </div>
      )}
      <div style={isMobile ? { ...styles.topBar, padding: '8px 12px' } : styles.topBar}>
        <div style={styles.welcomeSection}>
          {isMobile && (
            <button onClick={onMenuToggle} style={styles.hamburgerButton} aria-label="Toggle menu">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}
          {!isMobile && (
            <span style={styles.welcomeText}>Welcome, {u?.firstName || 'User'}</span>
          )}
        </div>
        <div style={isMobile ? { ...styles.actionsSection, gap: '8px', flexWrap: 'wrap' } : styles.actionsSection}>
          {/* Time Format Toggle */}
          <div style={styles.timeToggle}>
            {!isMobile && <span style={styles.timeLabel}>Time:</span>}
            <div style={styles.toggleButtons}>
              <button
                onClick={() => setDisplayMode(DISPLAY_MODES.LOCAL)}
                style={{
                  ...styles.toggleButton,
                  ...(displayMode === DISPLAY_MODES.LOCAL ? styles.toggleButtonActive : {})
                }}
              >
                Local
              </button>
              <button
                onClick={() => setDisplayMode(DISPLAY_MODES.UTC)}
                style={{
                  ...styles.toggleButton,
                  ...(displayMode === DISPLAY_MODES.UTC ? styles.toggleButtonActive : {})
                }}
              >
                UTC
              </button>
            </div>
          </div>

          {/* Practice Selector (only in practice view) */}
          {!isAdminView && (
            <div style={styles.dropdownContainer} ref={practiceMenuRef}>
              <button
                onClick={() => setShowPracticeMenu(!showPracticeMenu)}
                style={styles.practiceButton}
              >
                {!isMobile && <span style={styles.practiceLabel}>Practice:</span>}
                <span style={styles.practiceValue}>{getPracticeName()}</span>
                <ChevronDownIcon />
              </button>
              {showPracticeMenu && (
                <div style={styles.dropdownMenu}>
                  {practices.map((practice) => (
                    <button
                      key={practice.id}
                      onClick={() => handlePracticeChange(practice.id)}
                      style={{
                        ...styles.dropdownItem,
                        ...(practice.id === selectedPracticeId ? styles.dropdownItemActive : {})
                      }}
                    >
                      {practice.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Admin/Practice View Switch */}
          {isSuperAdmin() && (
            <NavLink
              to={isAdminView ? '/practice-view' : '/admin'}
              style={styles.switchButton}
            >
              {isAdminView ? 'Practice View' : 'Admin Console'}
            </NavLink>
          )}

          {/* User Menu */}
          <div style={styles.dropdownContainer} ref={userMenuRef}>
            <button
              data-testid="user-menu"
              onClick={() => setShowUserMenu(!showUserMenu)}
              style={styles.userButton}
            >
              <div style={styles.userAvatar}>
                <span style={styles.userInitials}>{getUserInitials()}</span>
              </div>
              {!isMobile && <span style={styles.userName}>{getUserDisplayName()}</span>}
              <ChevronDownIcon />
            </button>
            {showUserMenu && (
              <div style={styles.dropdownMenu}>
                <div style={styles.dropdownHeader}>
                  <strong>{user?.first_name} {user?.last_name}</strong>
                  <span style={styles.dropdownEmail}>{user?.email}</span>
                </div>
                <div style={styles.dropdownDivider} />
                <button style={styles.dropdownItem} onClick={() => {
                  setShowUserMenu(false);
                  navigate('/practice-view/profile');
                }}>
                  <UserIcon />
                  Profile Settings
                </button>
                <button style={styles.dropdownItem} onClick={() => {
                  setShowUserMenu(false);
                  navigate('/practice-view/activity');
                }}>
                  <ClockIcon />
                  Activity History
                </button>
                <div style={styles.dropdownDivider} />
                <button
                  data-testid="logout"
                  onClick={() => {
                    setShowUserMenu(false);
                    clearSelection();
                    logout();
                    navigate('/login');
                  }}
                  style={{ ...styles.dropdownItem, ...styles.dropdownItemDanger }}
                >
                  <LogoutIcon />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

// Icon Components
function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '4px' }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

const styles = {
  testModeBanner: {
    background: '#fbbf24',
    color: '#78350f',
    textAlign: 'center',
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.5px'
  },
  header: {
    background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
    color: '#fff',
    boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
    flexShrink: 0
  },
  headerAdmin: {
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
    color: '#fff',
    boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
    flexShrink: 0
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 24px'
  },
  welcomeSection: {
    display: 'flex',
    alignItems: 'center',
  },
  welcomeText: {
    fontSize: '15px',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  actionsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  timeToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  timeLabel: {
    fontSize: '14px',
    opacity: 0.9,
    fontWeight: '500'
  },
  toggleButtons: {
    display: 'flex',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '6px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  },
  toggleButton: {
    padding: '5px 10px',
    fontSize: '13px',
    fontWeight: '500',
    border: 'none',
    background: 'transparent',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  toggleButtonActive: {
    backgroundColor: '#fff',
    color: '#7c3aed',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
  },
  dropdownContainer: {
    position: 'relative'
  },
  practiceButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.15s ease'
  },
  practiceLabel: {
    opacity: 0.9,
    marginRight: '4px'
  },
  practiceValue: {
    fontWeight: '600'
  },
  switchButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 14px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 10px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    borderRadius: '6px',
    fontSize: '14px',
    transition: 'background-color 0.15s ease'
  },
  userAvatar: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(255, 255, 255, 0.3)'
  },
  userInitials: {
    fontSize: '12px',
    fontWeight: '600'
  },
  userName: {
    fontWeight: '500',
    fontSize: '14px'
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    minWidth: '220px',
    zIndex: 1000,
    overflow: 'hidden'
  },
  dropdownHeader: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: '#f9fafb'
  },
  dropdownEmail: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '2px'
  },
  dropdownDivider: {
    height: '1px',
    backgroundColor: '#e5e7eb'
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#374151',
    fontSize: '14px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s ease'
  },
  dropdownItemActive: {
    backgroundColor: '#eff6ff',
    color: '#1d4ed8'
  },
  dropdownItemDanger: {
    color: '#dc2626'
  },
  hamburgerButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
  }
};

export default PlatformHeader;
